import { useCallback, useEffect, useRef, useState } from "react";

interface UseRevealOptions {
  threshold?: number;
  rootMargin?: string;
  once?: boolean;
}

/**
 * Observes an element and flips `isVisible` when it enters the viewport.
 * Defaults to once-only reveal for entrance animations.
 * Failsafe: never leaves content stuck at opacity 0.
 */
export const useReveal = <T extends HTMLElement = HTMLDivElement>({
  threshold = 0.01,
  rootMargin = "0px 0px 0px 0px",
  once = true,
}: UseRevealOptions = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const visibleRef = useRef(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);

  const reveal = useCallback(() => {
    if (visibleRef.current) return;
    visibleRef.current = true;
    setIsVisible(true);
    if (fallbackTimerRef.current != null) {
      window.clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
    if (once) {
      observerRef.current?.disconnect();
    }
  }, [once]);

  const setRef = useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect();
      if (fallbackTimerRef.current != null) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }

      if (!node || (once && visibleRef.current)) {
        if (node && visibleRef.current) {
          setIsVisible(true);
        }
        return;
      }

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            reveal();
          } else if (!once) {
            visibleRef.current = false;
            setIsVisible(false);
          }
        },
        { threshold, rootMargin }
      );

      observerRef.current.observe(node);

      requestAnimationFrame(() => {
        const rect = node.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (rect.top < vh && rect.bottom > 0) {
          reveal();
        }
      });

      // Failsafe: never leave content invisible if IO misses
      fallbackTimerRef.current = window.setTimeout(() => {
        reveal();
      }, 400);
    },
    [threshold, rootMargin, once, reveal]
  );

  useEffect(
    () => () => {
      observerRef.current?.disconnect();
      if (fallbackTimerRef.current != null) {
        window.clearTimeout(fallbackTimerRef.current);
      }
    },
    []
  );

  return { ref: setRef, isVisible };
};
